import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { DogProfileExtended } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  dog: DogProfileExtended | null;
  activeDogCount: number;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function DeleteDogModal({ open, onClose, dog, activeDogCount, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLastActive = activeDogCount <= 1;

  const handleConfirm = async () => {
    if (!dog) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/account/dogs/soft-delete", {
        method: "POST",
        body: { dogId: dog.id },
      });
      if (!res.ok) throw new Error(res.error || "Failed to delete dog");
      onSuccess(`${dog.name || "Dog"} soft-deleted successfully`);
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to delete dog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.red }}>Delete Dog</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isLastActive ? (
          <Alert severity="error">
            Cannot delete the last active dog. There must be at least one active dog profile.
          </Alert>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will soft-delete <strong>{dog?.name}</strong>. The dog profile can be restored later.
            </Alert>
            <Typography sx={{ fontSize: 13, color: COLORS.textMuted }}>
              The dog&apos;s nutrition data and subscription associations will be preserved but the profile
              will be hidden from the customer&apos;s active view.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || isLastActive}
          sx={{ bgcolor: COLORS.red, "&:hover": { bgcolor: "#B43A30" }, textTransform: "none" }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
